import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  History as HistoryIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import PermissionAuditLogDialog from "./PermissionAuditLogDialog";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "store";
import { getCsrfToken, formatDate } from "../../utils";


import { can } from '../../utils/access';
import { flattenDrfError } from '../../utils/apiUtils';
import { getRoleLabel } from '../../utils/roles';
function TeamMembersTab({ t, readOnly = false, onShowRoles }) {
  const navigate = useNavigate();
  const { currentOrganization, user } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("foreman");
  const [linkableEmployees, setLinkableEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);
  const [seatStatus, setSeatStatus] = useState(null);

  const isAdmin = can(currentOrganization, "team.roles");
  const canManageTeam = isAdmin && !readOnly;

  useEffect(() => {
    if (currentOrganization?.organization_id) {
      fetchPermissions();
      fetchInvitations();
    }
  }, [currentOrganization?.organization_id]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/organization-permissions/`, {
        headers: { "X-Organization-Id": currentOrganization.organization_id },
        params: { organization: currentOrganization.organization_id },
      });
      setPermissions(response.data);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setSnackbar({
        open: true,
        message: t("Failed to load permissions"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`/api/organization-invitations/`, {
        headers: { "X-Organization-Id": currentOrganization.organization_id },
        params: { organization: currentOrganization.organization_id },
      });
      setInvitations(response.data);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
    }
  };

  const fetchLinkableEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(`/api/employees/unlinked/`, {
        headers: { "X-Organization-Id": currentOrganization.organization_id },
        params: { organization: currentOrganization.organization_id },
      });
      setLinkableEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch linkable employees:", error);
      setLinkableEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchSeatStatus = async () => {
    try {
      const response = await axios.get('/api/payments/seats/');
      setSeatStatus(response.data);
    } catch (err) {
      setSeatStatus(null);
    }
  };

  const handleOpenInviteDialog = () => {
    setInviteDialogOpen(true);
    fetchLinkableEmployees();
    fetchSeatStatus();
  };

  // The first two accountants take no seat, and an accountant never gets an employee record,
  // so a full plan must not stop the admin from inviting one.
  const isAccountantInvite = inviteRole === "accountant";
  const canAddUser =
    seatStatus === null ||
    seatStatus.is_unlimited ||
    (isAccountantInvite ? seatStatus.can_add_accountant : seatStatus.can_add_seat);
  const canAddEmployee =
    seatStatus === null || seatStatus.can_add_employee || seatStatus.is_unlimited || isAccountantInvite;
  // Can invite if: user limit OK AND (employee limit OK OR linking to existing employee)
  const canInvite = canAddUser && (canAddEmployee || selectedEmployee !== null);

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      setSnackbar({
        open: true,
        message: t("Email is required"),
        severity: "error",
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        `/api/organization-permissions/`,
        {
          organization: currentOrganization.organization_id,
          user_email: inviteEmail,
          role: inviteRole,
          employee_id: selectedEmployee?.id || null,
        },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );

      // Check if invitation was sent (for new users) or user was added directly
      const invitationSent = response.data.invitation_sent;
      setSnackbar({
        open: true,
        message: invitationSent
          ? t("Invitation email sent successfully")
          : t("User added successfully"),
        severity: "success",
      });
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteRole("employee");
      setSelectedEmployee(null);
      fetchPermissions();
      fetchInvitations();
    } catch (error) {
      console.error("Failed to invite user:", error);
      // The backend refuses an invite on user_email, employee_id or non_field_errors
      // (seat and employee limits). Reading only user_email left the admin with the
      // generic message and no idea what to fix.
      const message = flattenDrfError(error, t("Failed to invite user"), [
        "user_email",
        "employee_id",
      ]);
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (permission, newRole) => {
    try {
      await axios.patch(
        `/api/organization-permissions/${permission.id}/`,
        { role: newRole },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      setSnackbar({
        open: true,
        message: t("Role updated successfully"),
        severity: "success",
      });
      fetchPermissions();
    } catch (error) {
      console.error("Failed to update role:", error);
      const message =
        error.response?.data?.detail || t("Failed to update role");
      setSnackbar({ open: true, message, severity: "error" });
    }
  };

  const handleRemoveUser = async () => {
    if (!selectedPermission) return;

    try {
      setSubmitting(true);
      await axios.delete(
        `/api/organization-permissions/${selectedPermission.id}/`,
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      setSnackbar({
        open: true,
        message: t("User removed successfully"),
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedPermission(null);
      fetchPermissions();
    } catch (error) {
      console.error("Failed to remove user:", error);
      const message =
        error.response?.data?.detail || t("Failed to remove user");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async (invitation) => {
    try {
      await axios.post(
        `/api/organization-invitations/${invitation.id}/resend/`,
        {},
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      setSnackbar({
        open: true,
        message: t("Invitation resent successfully"),
        severity: "success",
      });
      fetchInvitations();
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      const message =
        error.response?.data?.detail || t("Failed to resend invitation");
      setSnackbar({ open: true, message, severity: "error" });
    }
  };

  const handleRevokeInvitation = async () => {
    if (!selectedInvitation) return;

    try {
      setSubmitting(true);
      await axios.post(
        `/api/organization-invitations/${selectedInvitation.id}/revoke/`,
        {},
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      setSnackbar({
        open: true,
        message: t("Invitation revoked"),
        severity: "success",
      });
      setRevokeDialogOpen(false);
      setSelectedInvitation(null);
      fetchInvitations();
    } catch (error) {
      console.error("Failed to revoke invitation:", error);
      const message =
        error.response?.data?.detail || t("Failed to revoke invitation");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteDialog = (permission) => {
    setSelectedPermission(permission);
    setDeleteDialogOpen(true);
  };

  const openRevokeDialog = (invitation) => {
    setSelectedInvitation(invitation);
    setRevokeDialogOpen(true);
  };

  const getInitials = (name, email) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return email ? email[0].toUpperCase() : "?";
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Add Button - Responsive */}
      <Box sx={{
        display: "flex",
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: "space-between",
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {t("Team Members")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Manage who has access to this organization")}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          {canManageTeam && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setAuditLogDialogOpen(true)}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              {t("Audit Log")}
            </Button>
          )}
          {canManageTeam && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenInviteDialog}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              {t("Invite User")}
            </Button>
          )}
        </Box>
      </Box>

      {/* Permissions Table - Desktop */}
      <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("User")}</TableCell>
              <TableCell>{t("Email")}</TableCell>
              <TableCell>{t("Role")}</TableCell>
              <TableCell>{t("Added")}</TableCell>
              {canManageTeam && <TableCell align="right">{t("Actions")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((permission) => (
              <TableRow key={permission.id} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
                      {getInitials(permission.user?.name, permission.user?.email)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>
                      {permission.user?.name || "-"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{permission.user?.email}</Typography>
                </TableCell>
                <TableCell>
                  {canManageTeam && permission.user?.id !== user?.id ? (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={permission.role}
                        onChange={(e) => handleRoleChange(permission, e.target.value)}
                      >
                        <MenuItem value="admin">{t("Admin")}</MenuItem>
                        <MenuItem value="foreman">{t("Foreman")}</MenuItem>
                        <MenuItem value="employee">{t("Employee")}</MenuItem>
                        <MenuItem value="accountant">{t("Accountant")}</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip
                      label={getRoleLabel(permission.role, t)}
                      size="small"
                      color={permission.role === "admin" ? "primary" : "default"}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(permission.created_at)}
                  </Typography>
                </TableCell>
                {canManageTeam && (
                  <TableCell align="right">
                    {permission.user?.id !== user?.id && (
                      <Tooltip title={t("Remove user")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(permission)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {permissions.length === 0 && (
              <TableRow>
                <TableCell colSpan={canManageTeam ? 5 : 4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {t("No users found")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Permissions Cards - Mobile */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {permissions.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t("No users found")}
            </Typography>
          </Paper>
        ) : (
          permissions.map((permission) => (
            <Paper
              key={permission.id}
              variant="outlined"
              sx={{ p: 2, mb: 1.5 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main", flexShrink: 0 }}>
                    {getInitials(permission.user?.name, permission.user?.email)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {permission.user?.name || "-"}
                      </Typography>
                      <Chip
                        label={getRoleLabel(permission.role, t)}
                        size="small"
                        color={permission.role === "admin" ? "primary" : "default"}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {permission.user?.email}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {t("Added")}: {formatDate(permission.created_at)}
                    </Typography>
                  </Box>
                </Box>
                {canManageTeam && permission.user?.id !== user?.id && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => openDeleteDialog(permission)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {canManageTeam && permission.user?.id !== user?.id && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={permission.role}
                      onChange={(e) => handleRoleChange(permission, e.target.value)}
                    >
                      <MenuItem value="admin">{t("Admin")}</MenuItem>
                      <MenuItem value="foreman">{t("Foreman")}</MenuItem>
                      <MenuItem value="employee">{t("Employee")}</MenuItem>
                      <MenuItem value="accountant">{t("Accountant")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Paper>
          ))
        )}
      </Box>

      {/* Pending Invitations Section */}
      {canManageTeam && invitations.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mb: 2 }}>
            {t("Pending Invitations")}
          </Typography>

          {/* Invitations Table - Desktop */}
          <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("Email")}</TableCell>
                  <TableCell>{t("Role")}</TableCell>
                  <TableCell>{t("Linked Employee")}</TableCell>
                  <TableCell>{t("Sent")}</TableCell>
                  <TableCell>{t("Expires")}</TableCell>
                  <TableCell align="right">{t("Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map((invitation) => (
                  <TableRow key={invitation.id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: "grey.400" }}>
                          {invitation.email[0].toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">{invitation.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(invitation.role, t)}
                        size="small"
                        color={invitation.role === "admin" ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {invitation.employee_name ? (
                        <Chip label={invitation.employee_name} size="small" color="info" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(invitation.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={invitation.is_expired ? "error" : "text.secondary"}
                      >
                        {formatDate(invitation.expires_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("Resend invitation")}>
                        <IconButton
                          size="small"
                          onClick={() => handleResendInvitation(invitation)}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("Revoke invitation")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openRevokeDialog(invitation)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Invitations Cards - Mobile */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {invitations.map((invitation) => (
              <Paper
                key={invitation.id}
                variant="outlined"
                sx={{ p: 2, mb: 1.5 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: "grey.400", flexShrink: 0 }}>
                      {invitation.email[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {invitation.email}
                        </Typography>
                        <Chip
                          label={getRoleLabel(invitation.role, t)}
                          size="small"
                          color={invitation.role === "admin" ? "primary" : "default"}
                        />
                      </Box>
                      {invitation.employee_name && (
                        <Chip
                          label={invitation.employee_name}
                          size="small"
                          color="info"
                          sx={{ mb: 0.5 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t("Sent")}: {formatDate(invitation.created_at)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={invitation.is_expired ? "error.main" : "text.secondary"}
                        sx={{ display: 'block' }}
                      >
                        {t("Expires")}: {formatDate(invitation.expires_at)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleResendInvitation(invitation)}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => openRevokeDialog(invitation)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: '12px', sm: '16px' },
            m: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogTitle>{t("Invite User")}</DialogTitle>
        <DialogContent>
          {!canAddUser && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setInviteDialogOpen(false);
                    navigate('/settings?tab=billing');
                  }}
                >
                  {t('Upgrade Plan')}
                </Button>
              }
            >
              {t('User limit reached. Your plan allows {{limit}} users and you have {{count}}.', {
                limit: seatStatus?.seat_limit || 0,
                count: seatStatus?.seats_total_used || 0,
              })}
            </Alert>
          )}
          {canAddUser && !canAddEmployee && !selectedEmployee && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setInviteDialogOpen(false);
                    navigate('/settings?tab=billing');
                  }}
                >
                  {t('Upgrade Plan')}
                </Button>
              }
            >
              {t('Employee limit reached. Your plan allows {{limit}} employees and you have {{count}}. Select an existing employee to link, or upgrade your plan.', {
                limit: seatStatus?.employees_limit || 0,
                count: seatStatus?.employees_count || 0,
              })}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            {t("Enter the email address of the user you want to invite. If they already have an account, they will be added immediately. Otherwise, they will receive an invitation email to create an account.")}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t("Email Address")}
            type="email"
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>{t("Role")}</InputLabel>
            <Select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              label={t("Role")}
            >
              <MenuItem value="admin">{t("Admin")}</MenuItem>
              <MenuItem value="foreman">{t("Foreman")}</MenuItem>
              <MenuItem value="employee">{t("Employee")}</MenuItem>
              <MenuItem value="accountant">{t("Accountant")}</MenuItem>
            </Select>
          </FormControl>
          {isAccountantInvite && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("permissions.editor.accountantFreeSeats")}
            </Typography>
          )}
          {linkableEmployees.length > 0 && (
            <Autocomplete
              sx={{ mt: 2 }}
              options={linkableEmployees}
              getOptionLabel={(option) => option.name || ''}
              value={selectedEmployee}
              onChange={(e, value) => setSelectedEmployee(value)}
              loading={loadingEmployees}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="off"
                  label={t("Link to existing employee")}
                  placeholder={t("Optional: select employee to link")}
                  size="small"
                  helperText={t("If selected, the user's account will be linked to this existing employee record")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEmployees && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name}
                    </Typography>
                    {option.phone_number && (
                      <Typography variant="caption" color="text.secondary">
                        {option.phone_number}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t("permissions.editor.inviteHint")}{" "}
            <Button
              size="small"
              variant="text"
              sx={{ textTransform: "none", p: 0, minWidth: 0, verticalAlign: "baseline" }}
              onClick={() => {
                setInviteDialogOpen(false);
                onShowRoles?.();
              }}
            >
              {t("permissions.editor.inviteLink")}
            </Button>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>{t("Cancel")}</Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={submitting || !canInvite}
          >
            {submitting ? <CircularProgress size={20} /> : <span>{t("Invite")}</span>}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: '12px', sm: '16px' },
            m: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogTitle>{t("Remove User")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Are you sure you want to remove")} <strong>{selectedPermission?.user?.email}</strong>{" "}
            {t("from this organization? They will lose access immediately.")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("Cancel")}</Button>
          <Button
            onClick={handleRemoveUser}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : <span>{t("Remove")}</span>}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Invitation Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: '12px', sm: '16px' },
            m: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogTitle>{t("Revoke Invitation")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Are you sure you want to revoke the invitation for")} <strong>{selectedInvitation?.email}</strong>?{" "}
            {t("They will no longer be able to use the invitation link.")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>{t("Cancel")}</Button>
          <Button
            onClick={handleRevokeInvitation}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : <span>{t("Revoke")}</span>}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log Dialog */}
      <PermissionAuditLogDialog
        open={auditLogDialogOpen}
        handleClose={() => setAuditLogDialogOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(TeamMembersTab);
